/**
 * Plotly Manager for Astronomical Visualizations
 * 
 * This module handles all Plotly.js interactions and plot management
 * for the astronomical imaging calculator.
 */

export class PlotlyManager {
    constructor() {
        this.plots = new Map();
        this.isInitialized = false;

        // Dark theme colors matching the Obsidian palette
        this.darkTheme = {
            paper_bgcolor: '#0a0a0a',
            plot_bgcolor: '#141414',
            font: {
                color: '#b0b0b0',
                family: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
            },
            title: {
                font: {
                    color: '#ffffff',
                    family: 'Space Grotesk, Inter, sans-serif'
                }
            },
            xaxis: {
                gridcolor: '#2a2a2a',
                linecolor: '#2a2a2a',
                tickcolor: '#707070',
                tickfont: { color: '#b0b0b0' },
                title: { font: { color: '#b0b0b0' } },
                zerolinecolor: '#3a3a3a'
            },
            yaxis: {
                gridcolor: '#2a2a2a',
                linecolor: '#2a2a2a',
                tickcolor: '#707070',
                tickfont: { color: '#b0b0b0' },
                title: { font: { color: '#b0b0b0' } },
                zerolinecolor: '#3a3a3a'
            },
            legend: {
                bgcolor: 'rgba(10, 10, 10, 0.9)',
                bordercolor: '#2a2a2a',
                font: { color: '#b0b0b0' }
            },
            colorway: ['#6b8cae', '#7d9bbe', '#4ade80', '#fbbf24', '#f87171', '#a78bfa', '#22d3d8', '#f472b6']
        };
    }

    /**
     * Initialize the Plotly manager
     */
    initialize() {
        if (this.isInitialized) {
            return;
        }

        // Check if Plotly is available
        if (typeof Plotly === 'undefined') {
            console.error('Plotly.js is not loaded');
            return;
        }

        this.isInitialized = true;
        console.log('Plotly manager initialized');
    }

    /**
     * Display a plot in the specified container
     * @param {string} containerId - HTML element ID
     * @param {Object} plotData - Plot data and layout
     * @param {Object} options - Additional options
     */
    async displayPlot(containerId, plotData, options = {}) {
        if (!this.isInitialized) {
            console.error('Plotly manager not initialized');
            return;
        }

        const container = document.getElementById(containerId);
        if (!container) {
            console.error(`Container with ID '${containerId}' not found`);
            return;
        }

        const isMobile = (typeof window !== 'undefined') && window.innerWidth <= 576;
        const defaultOptions = {
            responsive: true,
            displayModeBar: !isMobile,
            useResizeHandler: true,
            style: { width: "100%", height: "100%" },
            config: {
                responsive: true,
                displayModeBar: !isMobile
            }
        };

        const finalOptions = { ...defaultOptions, ...options };

        try {
            // Clear existing plot
            Plotly.purge(containerId);
            
            // Get container dimensions
            const containerRect = container.getBoundingClientRect();
            const containerWidth = containerRect.width || container.offsetWidth || 800;
            
            // Ensure layout has proper responsive settings
        // Build layout; for 3D scenes we enforce a square canvas to avoid egg-shape
        const has3D = Array.isArray(plotData.data) && plotData.data.some(tr => tr && (tr.type === 'scatter3d' || tr.type === 'surface' || tr.type === 'mesh3d'));
        // For 3D plots, allow scroll wheel zoom when cursor is over the plot; disable otherwise
        finalOptions.scrollZoom = has3D ? true : false;

        // Apply dark theme to layout
        const responsiveLayout = {
            ...plotData.layout,
            paper_bgcolor: this.darkTheme.paper_bgcolor,
            plot_bgcolor: this.darkTheme.plot_bgcolor,
            font: { ...this.darkTheme.font, ...(plotData.layout.font || {}) },
            colorway: this.darkTheme.colorway
        };

        // Apply dark theme to axes if they exist
        if (responsiveLayout.xaxis) {
            responsiveLayout.xaxis = { ...this.darkTheme.xaxis, ...responsiveLayout.xaxis };
        }
        if (responsiveLayout.yaxis) {
            responsiveLayout.yaxis = { ...this.darkTheme.yaxis, ...responsiveLayout.yaxis };
        }

        // Apply dark theme to legend if it exists
        if (responsiveLayout.legend) {
            responsiveLayout.legend = { ...this.darkTheme.legend, ...responsiveLayout.legend };
        } else {
            responsiveLayout.legend = this.darkTheme.legend;
        }

        // Apply dark theme to 3D scenes
        if (has3D && responsiveLayout.scene) {
            responsiveLayout.scene = {
                ...responsiveLayout.scene,
                xaxis: { ...responsiveLayout.scene.xaxis, gridcolor: '#2a2a2a', color: '#b0b0b0' },
                yaxis: { ...responsiveLayout.scene.yaxis, gridcolor: '#2a2a2a', color: '#b0b0b0' },
                zaxis: { ...responsiveLayout.scene.zaxis, gridcolor: '#2a2a2a', color: '#b0b0b0' }
            };
        }

        // Apply dark theme to polar plots (for Sky Path 2D view)
        if (responsiveLayout.polar) {
            responsiveLayout.polar = {
                ...responsiveLayout.polar,
                bgcolor: this.darkTheme.plot_bgcolor,
                radialaxis: {
                    ...responsiveLayout.polar.radialaxis,
                    gridcolor: '#2a2a2a',
                    linecolor: '#2a2a2a',
                    tickfont: { color: '#b0b0b0' }
                },
                angularaxis: {
                    ...responsiveLayout.polar.angularaxis,
                    gridcolor: '#2a2a2a',
                    linecolor: '#2a2a2a',
                    tickfont: { ...responsiveLayout.polar.angularaxis?.tickfont, color: '#ffffff' }
                }
            };
        }

        if (has3D && containerId !== 'skyPathPlot') {
            const size = this.calculateSquareSize(containerWidth);
            responsiveLayout.autosize = false;
            responsiveLayout.width = size;
            responsiveLayout.height = size; // enforce square
        } else {
            responsiveLayout.autosize = true;
            responsiveLayout.width = Math.max(320, containerWidth - 24);
            responsiveLayout.height = plotData.layout.height || 500;
        }
            
            // Create new plot
            Plotly.newPlot(containerId, plotData.data, responsiveLayout, finalOptions);
            
        // Force resize after a short delay to ensure proper rendering
        setTimeout(() => {
            // For most 3D plots maintain square; skip skyPathPlot (handled externally)
            if (has3D && containerId !== 'skyPathPlot') {
                try {
                    const redrawWidth = container.getBoundingClientRect().width || container.offsetWidth;
                    const size2 = this.calculateSquareSize(redrawWidth);
                    Plotly.relayout(containerId, { width: size2, height: size2 });
                } catch (_) { Plotly.Plots.resize(containerId); }
            } else {
                Plotly.Plots.resize(containerId);
            }
        }, 100);
            
            // Store plot reference
            this.plots.set(containerId, {
                data: plotData.data,
                layout: plotData.layout,
                options: finalOptions
            });
            

            console.log(`Plot displayed in container: ${containerId}`);
        } catch (error) {
            console.error('Error displaying plot:', error);
        }
    }

    /**
     * Update an existing plot
     * @param {string} containerId - HTML element ID
     * @param {Object} plotData - New plot data
     * @param {Object} options - Additional options
     */
    updatePlot(containerId, plotData, options = {}) {
        if (!this.isInitialized) {
            console.error('Plotly manager not initialized');
            return;
        }

        const container = document.getElementById(containerId);
        if (!container) {
            console.error(`Container with ID '${containerId}' not found`);
            return;
        }

        try {
            // Update the plot
            Plotly.react(containerId, plotData.data, plotData.layout, options);
            
            // Update stored reference
            this.plots.set(containerId, {
                data: plotData.data,
                layout: plotData.layout,
                options: options
            });

            console.log(`Plot updated in container: ${containerId}`);
        } catch (error) {
            console.error('Error updating plot:', error);
        }
    }

    /**
     * Add a trace to an existing plot
     * @param {string} containerId - HTML element ID
     * @param {Object} trace - New trace data
     */
    addTrace(containerId, trace) {
        if (!this.isInitialized) {
            console.error('Plotly manager not initialized');
            return;
        }

        try {
            Plotly.addTraces(containerId, trace);
            console.log(`Trace added to plot: ${containerId}`);
        } catch (error) {
            console.error('Error adding trace:', error);
        }
    }

    /**
     * Remove traces from a plot
     * @param {string} containerId - HTML element ID
     * @param {Array} traceIndices - Indices of traces to remove
     */
    removeTraces(containerId, traceIndices) {
        if (!this.isInitialized) {
            console.error('Plotly manager not initialized');
            return;
        }

        try {
            Plotly.deleteTraces(containerId, traceIndices);
            console.log(`Traces removed from plot: ${containerId}`);
        } catch (error) {
            console.error('Error removing traces:', error);
        }
    }

    /**
     * Resize all plots
     */
    resizeAllPlots() {
        if (!this.isInitialized) {
            return;
        }

        this.plots.forEach((plot, containerId) => {
            try {
                const container = document.getElementById(containerId);
                if (container) {
                    const plot = this.plots.get(containerId);
                    const has3D = plot && Array.isArray(plot.data) && plot.data.some(tr => tr && (tr.type === 'scatter3d' || tr.type === 'surface' || tr.type === 'mesh3d'));
                    const containerWidth = container.offsetWidth || container.getBoundingClientRect().width;
                    if (containerWidth > 0) {
                        if (has3D && containerId !== 'skyPathPlot') {
                            const size = this.calculateSquareSize(containerWidth);
                            Plotly.relayout(containerId, { width: size, height: size, autosize: false }).then(() => {
                                Plotly.Plots.resize(containerId);
                            }).catch(() => Plotly.Plots.resize(containerId));
                        } else {
                            const update = { width: containerWidth - 40, autosize: true };
                            Plotly.relayout(containerId, update).then(() => {
                                Plotly.Plots.resize(containerId);
                            }).catch(() => Plotly.Plots.resize(containerId));
                        }
                    } else {
                        Plotly.Plots.resize(containerId);
                    }
                }
            } catch (error) {
                console.error(`Error resizing plot ${containerId}:`, error);
            }
        });
    }

    /**
     * Compute an appropriate square canvas size for 3D plots so they stay within
     * their container on smaller screens while remaining readable on larger ones.
     * @param {number} containerWidth - Width available to the plot container
     * @returns {number} Square dimension to use for width/height
     */
    calculateSquareSize(containerWidth) {
        const width = Math.max(0, Math.floor(containerWidth || 0));

        if (width === 0) {
            return 400; // Fallback until DOM has a meaningful width
        }

        const padding = width > 40 ? 40 : 0; // allow some breathing room inside the card
        const size = width - padding;

        // Keep the canvas responsive by following the container width closely,
        // but guarantee a minimum so the scene remains legible on tiny screens.
        return Math.max(240, size);
    }

    /**
     * Get plot data for a container
     * @param {string} containerId - HTML element ID
     * @returns {Object|null} Plot data or null
     */
    getPlotData(containerId) {
        return this.plots.get(containerId) || null;
    }

    /**
     * Clear a specific plot
     * @param {string} containerId - HTML element ID
     */
    clearPlot(containerId) {
        if (!this.isInitialized) {
            return;
        }

        try {
            Plotly.purge(containerId);
            this.plots.delete(containerId);
            console.log(`Plot cleared: ${containerId}`);
        } catch (error) {
            console.error(`Error clearing plot ${containerId}:`, error);
        }
    }

    /**
     * Clear all plots
     */
    clearAllPlots() {
        if (!this.isInitialized) {
            return;
        }

        this.plots.forEach((plot, containerId) => {
            this.clearPlot(containerId);
        });
    }

    /**
     * Download plot as image
     * @param {string} containerId - HTML element ID
     * @param {string} format - Image format (png, jpeg, svg, webp)
     * @param {number} width - Image width
     * @param {number} height - Image height
     */
    downloadPlot(containerId, format = 'png', width = 800, height = 600) {
        if (!this.isInitialized) {
            console.error('Plotly manager not initialized');
            return;
        }

        try {
            Plotly.downloadImage(containerId, {
                format: format,
                width: width,
                height: height,
                filename: `astro_plot_${containerId}_${new Date().toISOString().split('T')[0]}`
            });
        } catch (error) {
            console.error('Error downloading plot:', error);
        }
    }

    /**
     * Get plot statistics
     * @returns {Object} Plot statistics
     */
    getStats() {
        return {
            totalPlots: this.plots.size,
            plotIds: Array.from(this.plots.keys()),
            initialized: this.isInitialized
        };
    }
}

export default PlotlyManager;
